import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Clock, Camera, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const shifts = [
  { id: 1, name: "Shift 1", time: "09:00 - 17:00", color: "from-blue-500 to-blue-600" },
  { id: 2, name: "Shift 2", time: "17:00 - 01:00", color: "from-purple-500 to-purple-600" },
  { id: 3, name: "Shift 3", time: "01:00 - 09:00", color: "from-cyan-500 to-cyan-600" },
];

export default function EmployeeDashboard() {
  const [user, setUser] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (!token || !userData) {
      navigate("/login/employee");
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    checkTodayAttendance(token);
  }, [navigate]);

  const checkTodayAttendance = async (token) => {
    try {
      const response = await axios.get(`${API}/attendance/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setTodayAttendance(response.data);
      }
    } catch (error) {
      console.log("No attendance today");
    }
  };

  const handleShiftClick = (shift) => {
    if (todayAttendance) {
      toast.warning("You have already checked in today!");
      return;
    }
    setSelectedShift(shift);
    setShowVerification(true);
    startCamera();
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      toast.error("Cannot access camera");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const handleVerify = async () => {
    stopCamera();
    setShowVerification(false);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/attendance`,
        { shift: selectedShift.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        checkTodayAttendance(token);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success("Successfully logged out");
    navigate("/login/employee");
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const getShiftGreeting = () => {
    if (!selectedShift) return "";
    const shiftName = selectedShift.name;
    const shiftTime = selectedShift.time;
    
    return `Happy working on ${shiftName} (${shiftTime})! Have a great day!`;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employee-dashboard">
      <div className="max-w-4xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6 fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                {getGreeting()}, {user.name}!
              </h1>
              <p className="text-slate-600 mt-1">{user.position}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-600 hover:text-red-600 hover:bg-red-50"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Attendance Status */}
        {todayAttendance ? (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-6 slide-up" data-testid="attendance-status">
            <div className="flex items-start gap-4">
              <div className="bg-green-500 rounded-full p-3">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900 mb-1">
                  You have already checked in today
                </h3>
                <p className="text-green-700">
                  Shift {todayAttendance.shift} - {shifts[todayAttendance.shift - 1].time}
                </p>
                <p className="text-sm text-green-600 mt-1 font-mono">
                  Check-in: {new Date(todayAttendance.check_in_time).toLocaleTimeString('en-US')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-6 slide-up">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-blue-900">
                  Please Select Your Shift
                </h3>
                <p className="text-blue-700 text-sm">Choose your work shift for today</p>
              </div>
            </div>
          </div>
        )}

        {/* Shift Selection */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">Select Shift</h2>
          <div className="grid gap-4">
            {shifts.map((shift, index) => (
              <button
                key={shift.id}
                onClick={() => handleShiftClick(shift)}
                disabled={todayAttendance !== null}
                className={`relative group overflow-hidden border rounded-xl p-6 text-left transition-all hover:shadow-xl ${
                  todayAttendance
                    ? "opacity-50 cursor-not-allowed bg-slate-100 border-slate-200"
                    : "bg-white border-slate-200 hover:border-blue-500 hover-lift cursor-pointer"
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`shift-${shift.id}-card`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">
                      {shift.name}
                    </h3>
                    <p className="text-slate-600 font-mono text-lg">{shift.time}</p>
                  </div>
                  <div
                    className={`w-16 h-16 rounded-full bg-gradient-to-br ${shift.color} flex items-center justify-center`}
                  >
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                </div>
                {!todayAttendance && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${shift.color} transform scale-x-0 group-hover:scale-x-100 transition-transform`}
                  ></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Face Verification Modal */}
      <Dialog open={showVerification} onOpenChange={(open) => {
        if (!open) {
          stopCamera();
          setShowVerification(false);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="face-verification-modal">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Face Verification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
                data-testid="camera-video"
              />
              <div className="absolute inset-0 border-4 border-cyan-500 m-8 rounded-lg scanning-animation" />
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-cyan-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                Scanning...
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  stopCamera();
                  setShowVerification(false);
                }}
                variant="outline"
                className="flex-1"
                data-testid="cancel-verification-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerify}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                data-testid="confirm-verification-button"
              >
                <Camera className="w-4 h-4 mr-2" />
                Verify
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-md text-center" data-testid="success-modal">
          <div className="space-y-4 py-4">
            <div className="flex justify-center">
              <img
                src="https://images.unsplash.com/photo-1634869258987-f13de2902431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHwzZCUyMGhhbmQlMjB0aHVtYnMlMjB1cCUyMHN1Y2Nlc3MlMjBpbGx1c3RyYXRpb258ZW58MHx8fHwxNzY0MzgwODc5fDA&ixlib=rb-4.1.0&q=85"
                alt="Success"
                className="w-32 h-32 object-contain"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-green-600 mb-2">
                Check-in Successful!
              </h3>
              <p className="text-slate-700 text-lg">
                {getShiftGreeting()}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}